package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.LoginRequest
import se.mittpass.shared.api.RegisterRequest

private val authDialogScope = MainScope()

enum class AuthMode { LOGIN, SIGNUP, FORGOT_PASSWORD }

@Composable
fun AuthDialog(
    mode: AuthMode,
    onSuccess: () -> Unit,
    onSwitchMode: (AuthMode) -> Unit,
    onDismiss: () -> Unit,
    onTerms: () -> Unit = {},
    onDpa: () -> Unit = {}
) {
    // Overlay backdrop
    Div({
        onClick { onDismiss() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(13,13,24,0.55)"); property("backdrop-filter", "blur(6px)")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("padding", "1rem")
        }
    }) {
        // Card — stop propagation so clicks inside don't close
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "28px")
                property("padding", "2.5rem 2.25rem"); property("width", "100%"); property("max-width", "420px")
                property("box-shadow", "0 24px 80px rgba(13,13,24,0.22)")
                property("position", "relative")
            }
        }) {
            // Close button
            Button({
                onClick { onDismiss() }
                style {
                    property("position", "absolute"); property("top", "1.25rem"); property("right", "1.25rem")
                    property("background", "var(--rp-overlay)"); property("border", "none"); property("border-radius", "50%")
                    property("width", "36px"); property("height", "36px"); property("cursor", "pointer")
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    property("color", "var(--rp-subtle)"); property("font-size", "1rem")
                }
            }) { FaIcon("fa-solid fa-xmark") }

            // Logo + wordmark
            Div({
                style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
                    property("margin-bottom", "1.5rem")
                }
            }) {
                Img(src = "/mittpass_logo.svg", alt = "Mittpass") {
                    style { property("width", "32px"); property("height", "32px") }
                }
                Span({
                    style {
                        property("font-size", "1.1rem"); property("font-weight", "800")
                        property("color", "var(--rp-text)"); property("letter-spacing", "-0.3px")
                    }
                }) { Text("Mittpass") }
            }

            // Removed public registration tabs for launch
            // if (mode == AuthMode.SIGNUP) { ... }

            if (mode == AuthMode.LOGIN) {
                LoginForm(onSuccess, onSwitchMode)
            } else if (mode == AuthMode.SIGNUP) {
                SignUpForm(onSuccess, onTerms, onDpa)
            } else {
                ForgotPasswordForm(onSwitchMode)
            }
        }
    }
}

@Composable
private fun LoginForm(onSuccess: () -> Unit, onSwitchMode: (AuthMode) -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var showResend by remember { mutableStateOf(false) }
    var resendSent by remember { mutableStateOf(false) }

    H2({
        style {
            property("font-size", "1.6rem"); property("font-weight", "900"); property("color", "var(--rp-text)")
            property("margin", "0 0 1.5rem 0"); property("letter-spacing", "-0.4px")
        }
    }) { Text("Välkommen tillbaka") }

    error?.let { err ->
        Div({
            style {
                property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                property("padding", "0.75rem 1rem"); property("border-radius", "12px")
                property("margin-bottom", "0.5rem"); property("font-size", "0.88rem"); property("font-weight", "600")
            }
        }) { Text(err) }
    }

    if (showResend) {
        Div({ style { property("margin-bottom", "1rem") } }) {
            if (resendSent) {
                Div({ style { property("background", "var(--rp-ok-bg)"); property("color", "var(--rp-pine)"); property("padding", "0.75rem 1rem"); property("border-radius", "12px"); property("font-size", "0.88rem"); property("font-weight", "600") } }) {
                    Text("Nytt verifieringsmail skickat!")
                }
            } else {
                Button({
                    onClick {
                        authDialogScope.launch {
                            authRepo.resendVerification(email.trim())
                            resendSent = true
                        }
                    }
                    style {
                        property("background", "none"); property("border", "none"); property("color", "var(--rp-pine)")
                        property("font-size", "0.88rem"); property("font-weight", "700"); property("cursor", "pointer")
                        property("padding", "0"); property("font-family", "inherit")
                    }
                }) { Text("Skicka verifieringsmail igen") }
            }
        }
    }

    authField("E-post", "email", InputType.Email, email, "din@email.se") { email = it; error = null; showResend = false }
    authField("Lösenord", "login-password", InputType.Password, password, "••••••••") { password = it; error = null }
    Div({ style { property("text-align", "right"); property("margin-bottom", "1rem"); property("margin-top", "-0.5rem") } }) {
        Button({
            onClick { onSwitchMode(AuthMode.FORGOT_PASSWORD) }
            style {
                property("background", "none"); property("border", "none"); property("color", "var(--rp-pine)")
                property("font-size", "0.85rem"); property("font-weight", "600"); property("cursor", "pointer")
                property("padding", "0"); property("font-family", "inherit")
            }
        }) { Text("Glömt lösenord?") }
    }

    Button({
        onClick {
            if (email.isBlank() || password.isBlank()) { error = "Fyll i e-post och lösenord."; return@onClick }
            loading = true; error = null; showResend = false
            authDialogScope.launch {
                authRepo.login(LoginRequest(email.trim(), password)).fold(
                    onSuccess = { onSuccess() },
                    onFailure = { e ->
                        val msg = e.message ?: ""
                        error = when {
                            msg.contains("refused", true) || msg.contains("Failed to fetch", true) -> "Kunde inte ansluta till servern."
                            msg.contains("Verifiera", true) -> msg.also { showResend = true }
                            else -> "Felaktig e-post eller lösenord."
                        }
                        loading = false
                    }
                )
            }
        }
        if (loading) disabled()
        style { authSubmitStyle(loading) }
    }) { Text(if (loading) "Loggar in…" else "Logga in") }
}

@Composable
private fun SignUpForm(onSuccess: () -> Unit, onTerms: () -> Unit = {}, onDpa: () -> Unit = {}) {
    var orgName by remember { mutableStateOf("") }
    var workplaceName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var verifyPending by remember { mutableStateOf(false) }

    if (verifyPending) {
        Div({ style { property("text-align", "center"); property("padding", "1rem 0") } }) {
            Div({ style { property("margin-bottom", "1rem") } }) {
                FaIcon("fa-solid fa-envelope-open-text", size = "2.5rem", color = "var(--rp-pine)")
            }
            H2({ style { property("font-size", "1.4rem"); property("font-weight", "900"); property("color", "var(--rp-text)"); property("margin", "0 0 0.75rem 0") } }) {
                Text("Kolla din e-post!")
            }
            Div({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.95rem"); property("line-height", "1.6") } }) {
                Text("Vi har skickat ett verifieringsmail till ")
                Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(email) }
                Text(". Klicka på länken i mailet för att aktivera ditt konto.")
            }
        }
        return
    }

    H2({
        style {
            property("font-size", "1.6rem"); property("font-weight", "900"); property("color", "var(--rp-text)")
            property("margin", "0 0 1.5rem 0"); property("letter-spacing", "-0.4px")
        }
    }) { Text("Skapa konto") }

    error?.let { err ->
        Div({
            style {
                property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                property("padding", "0.75rem 1rem"); property("border-radius", "12px")
                property("margin-bottom", "1rem"); property("font-size", "0.88rem"); property("font-weight", "600")
            }
        }) { Text(err) }
    }

    authField("Företagsnamn", "org-name", InputType.Text, orgName, "Din organisation AB") { orgName = it; error = null }
    authField("Arbetsplats", "wp-name", InputType.Text, workplaceName, "T.ex. Restaurang Centrum") { workplaceName = it; error = null }
    authField("E-post", "signup-email", InputType.Email, email, "din@email.se") { email = it; error = null }
    authField("Lösenord", "signup-password", InputType.Password, password, "Minst 8 tecken") { password = it; error = null }

    Button({
        onClick {
            if (orgName.isBlank() || workplaceName.isBlank() || email.isBlank() || password.isBlank()) {
                error = "Fyll i alla fält."; return@onClick
            }
            if (!isValidPassword(password)) { error = "Lösenordet måste vara minst 8 tecken långt och innehålla en stor bokstav, en liten bokstav, en siffra samt ett specialtecken."; return@onClick }
            loading = true; error = null
            authDialogScope.launch {
                authRepo.register(RegisterRequest(orgName.trim(), workplaceName.trim(), email.trim(), password)).fold(
                    onSuccess = { authResponse ->
                        if (authResponse == null) {
                            // Email verification required
                            verifyPending = true
                            loading = false
                        } else {
                            onSuccess()
                        }
                    },
                    onFailure = { e ->
                        error = e.message?.take(120) ?: "Registrering misslyckades."
                        loading = false
                    }
                )
            }
        }
        if (loading || verifyPending) disabled()
        style { authSubmitStyle(loading) }
    }) { Text(if (loading) "Skapar konto…" else "Skapa konto") }

    P({
        style {
            property("margin-top", "0.75rem"); property("text-align", "center")
            property("color", "var(--rp-muted)"); property("font-size", "0.76rem"); property("line-height", "1.5")
        }
    }) {
        Text("Genom att registrera dig godkänner du våra ")
        A(attrs = {
            href("#")
            onClick { it.preventDefault(); onTerms() }
            style { property("color", "var(--rp-pine)"); property("font-weight", "600"); property("text-decoration", "none") }
        }) { Text("användarvillkor") }
        Text(" och ingår ett ")
        A(attrs = {
            href("#")
            onClick { it.preventDefault(); onDpa() }
            style { property("color", "var(--rp-pine)"); property("font-weight", "600"); property("text-decoration", "none") }
        }) { Text("personuppgiftsbiträdesavtal") }
        Text(".")
    }
}

@Composable
private fun authField(
    label: String,
    inputId: String,
    type: InputType<String>,
    value: String,
    placeholder: String,
    onInput: (String) -> Unit
) {
    Div({ style { property("margin-bottom", "1rem") } }) {
        Label(attrs = {
            attr("for", inputId)
            style {
                property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem")
                property("color", "var(--rp-text)"); property("text-transform", "uppercase")
                property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
            }
        }) { Text(label) }
        Input(type) {
            id(inputId)
            this.value(value)
            this.onInput { onInput(it.value) }
            attr("placeholder", placeholder)
            style {
                property("width", "100%"); property("padding", "0.8rem 1rem")
                property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                property("font-size", "0.95rem"); property("outline", "none")
                property("box-sizing", "border-box"); property("font-family", "inherit")
            }
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.authSubmitStyle(loading: Boolean) {
    property("width", "100%"); property("padding", "0.9rem")
    property("font-size", "1rem"); property("font-weight", "700")
    property("cursor", if (loading) "default" else "pointer")
    property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
    property("color", "white"); property("border", "none")
    property("border-radius", "50px"); property("margin-top", "0.5rem")
    property("letter-spacing", "0.2px")
    if (loading) property("opacity", "0.7")
}

@Composable
private fun ForgotPasswordForm(onSwitchMode: (AuthMode) -> Unit) {
    var email by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var success by remember { mutableStateOf(false) }

    if (success) {
        Div({ style { property("text-align", "center"); property("padding", "1rem 0") } }) {
            Div({ style { property("margin-bottom", "1rem") } }) {
                FaIcon("fa-solid fa-envelope-open-text", size = "2.5rem", color = "var(--rp-pine)")
            }
            H2({ style { property("font-size", "1.4rem"); property("font-weight", "900"); property("color", "var(--rp-text)"); property("margin", "0 0 0.75rem 0") } }) {
                Text("Kolla din e-post!")
            }
            Div({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.95rem"); property("line-height", "1.6"); property("margin-bottom", "1.5rem") } }) {
                Text("Vi har skickat en länk för att återställa ditt lösenord till ")
                Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(email) }
                Text(". Det kan ta några minuter innan det kommer fram.")
            }
            Button({
                onClick { onSwitchMode(AuthMode.LOGIN) }
                style { authSubmitStyle(false) }
            }) { Text("Tillbaka till inloggning") }
        }
        return
    }

    H2({
        style {
            property("font-size", "1.6rem"); property("font-weight", "900"); property("color", "var(--rp-text)")
            property("margin", "0 0 0.5rem 0"); property("letter-spacing", "-0.4px")
        }
    }) { Text("Glömt lösenordet?") }
    
    P({ style { property("margin", "0 0 1.5rem 0"); property("color", "var(--rp-subtle)"); property("font-size", "0.9rem") } }) {
        Text("Fyll i din e-postadress så skickar vi en länk för att återställa lösenordet.")
    }

    error?.let { err ->
        Div({
            style {
                property("background", "var(--rp-err-bg)"); property("color", "var(--rp-love)")
                property("padding", "0.75rem 1rem"); property("border-radius", "12px")
                property("margin-bottom", "1rem"); property("font-size", "0.88rem"); property("font-weight", "600")
            }
        }) { Text(err) }
    }

    authField("E-post", "forgot-email", InputType.Email, email, "din@email.se") { email = it; error = null }

    Button({
        onClick {
            if (email.isBlank()) { error = "Fyll i din e-postadress."; return@onClick }
            loading = true; error = null
            authDialogScope.launch {
                authRepo.forgotPassword(email.trim()).fold(
                    onSuccess = { success = true },
                    onFailure = { e ->
                        error = e.message ?: "Något gick fel."
                        loading = false
                    }
                )
            }
        }
        if (loading) disabled()
        style { authSubmitStyle(loading) }
    }) { Text(if (loading) "Skickar…" else "Skicka länk") }

    Div({ style { property("text-align", "center"); property("margin-top", "1.5rem") } }) {
        Button({
            onClick { onSwitchMode(AuthMode.LOGIN) }
            style {
                property("background", "none"); property("border", "none"); property("color", "var(--rp-pine)")
                property("font-size", "0.9rem"); property("font-weight", "600"); property("cursor", "pointer")
                property("padding", "0"); property("font-family", "inherit")
            }
        }) { Text("Tillbaka till inloggning") }
    }
}

private fun isValidPassword(password: String): Boolean {
    val length = password.length >= 8
    val hasDigit = password.any { it.isDigit() }
    val hasUpper = password.any { it.isUpperCase() }
    val hasLower = password.any { it.isLowerCase() }
    val hasSpecial = password.any { !it.isLetterOrDigit() }
    return length && hasDigit && hasUpper && hasLower && hasSpecial
}
